import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface LegalHRToolProps {
  title: string;
  description: string;
  icon: JSX.Element;
  color: string;
  comingSoon?: boolean;
  beta?: boolean;
  onClick?: () => void;
}

const LegalHRToolCard: React.FC<LegalHRToolProps> = ({ 
  title, 
  description, 
  icon, 
  color,
  comingSoon = false,
  beta = false,
  onClick 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className={`relative transform transition-all duration-300 ${comingSoon ? 'opacity-70' : 'hover:-translate-y-2 cursor-pointer'}`}
      onClick={comingSoon ? undefined : onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card with glass morphism */}
      <div className="
        relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg 
        border border-white/30 shadow-lg group transition-all duration-300
        hover:shadow-xl p-6 h-full
      ">
        {/* Gradient background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-10 group-hover:opacity-20 transition-opacity`}></div>
        
        {/* Icon */}
        <div className={`
          w-12 h-12 rounded-lg mb-4 flex items-center justify-center text-white 
          bg-gradient-to-br ${color} shadow-lg group-hover:scale-110 transition-transform
        `}>
          <span className="text-xl">{icon}</span>
        </div>
        
        {/* Content */}
        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
          {title}
          {comingSoon && (
            <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
              Coming Soon
            </span>
          )}
          {!comingSoon && beta && (
            <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              Beta
            </span>
          )}
        </h3>
        <p className="text-gray-600 group-hover:text-gray-800 transition-colors">
          {description}
        </p>
        
        {/* Hidden action button that appears on hover */}
        {!comingSoon && (
          <div className="
            absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 
            transition-opacity transform group-hover:translate-x-0 translate-x-4
          ">
            <button className="
              flex items-center justify-center w-8 h-8 rounded-full 
              bg-white/80 text-blue-600 shadow-md hover:bg-white transition-colors
            ">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const LegalHR: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background with gradient and mesh */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg==')]"></div>
      </div>
      
      {/* Main content */}
      <div className="container relative z-10 mx-auto px-4">
        {/* Decorative elements */}
        <div className="absolute top-20 left-12 w-64 h-64 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute -bottom-32 right-12 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      
        {/* Section Title with 3D effect */}
        <div className="relative mb-16 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-blue-600 inline-block">
              Legal & HR Tools
            </h2>
            <Link
              to="/help?article=legal-hr-tools"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              ❓ Help
            </Link>
          </div>
          <div className="h-1 w-40 mx-auto rounded-full bg-gradient-to-r from-teal-400 to-blue-500"></div>
          <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">Essential legal and HR documents customized for your business needs</p>
        </div>
      
        {/* Tools Grid with 3D Cards and Glass Morphism */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <LegalHRToolCard
            title="AI Support Desk"
            description="Enterprise support platform with AI responses, multi-channel inbox, SLA tracking, knowledge base, and unlimited agents. Manage customer support tickets efficiently."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>}
            color="from-blue-500 via-cyan-500 to-teal-500"
            onClick={() => navigate('/admin/support')}
          />
          <LegalHRToolCard
            title="Contract Analyzer"
            description="Analyze contracts for red flags, missing protections, and beneficial loopholes across UK, USA, EU, Canada, and Australia."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a7 7 0 100-14 7 7 0 000 14zm13 4l-6-6" />
            </svg>}
            color="from-sky-400 to-blue-500"
            beta
            onClick={() => navigate('/legal-hr/contract-analyzer')}
          />
          <LegalHRToolCard
            title="Privacy Policy Generator"
            description="Create a comprehensive privacy policy tailored to your business and compliant with multiple jurisdictions."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>}
            color="from-teal-400 to-teal-500"
            onClick={() => navigate('/legal-hr/privacy-policy-generator')}
          />
          
          <LegalHRToolCard
            title="Terms & Conditions Generator"
            description="Generate customized terms and conditions for your website or application with jurisdiction-specific clauses."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>}
            color="from-blue-400 to-blue-500"
            onClick={() => navigate('/legal-hr/terms-conditions-generator')}
          />
          
          <LegalHRToolCard
            title="Employee Handbook Creator"
            description="Create a comprehensive employee handbook with customizable policies and procedures for your organization."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>}
            color="from-indigo-400 to-purple-500"
            onClick={() => navigate('/legal-hr/employee-handbook-creator')}
          />
          
          <LegalHRToolCard
            title="Non-Disclosure Agreement"
            description="Generate a customized NDA to protect your confidential information when sharing with third parties."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
            </svg>}
            color="from-cyan-400 to-blue-500"
            onClick={() => navigate('/legal-hr/nda-generator')}
          />
          
          <LegalHRToolCard
            title="Contractor Agreement"
            description="Create a legally sound independent contractor agreement tailored to your specific business needs."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>}
            color="from-green-400 to-teal-500"
            onClick={() => navigate('/legal-hr/contractor-agreement-generator')}
          />
          
          <LegalHRToolCard
            title="HR Policy Generator"
            description="Generate comprehensive HR policies compliant with employment laws in your jurisdiction."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>}
            color="from-purple-400 to-indigo-500"
            onClick={() => navigate('/legal-hr/hr-policy-generator')}
          />
          
          <LegalHRToolCard
            title="Staff Management"
            description="Manage your team with staff profiles, emergency contacts, work rotas, holiday schedules, and coverage tracking."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>}
            color="from-pink-400 to-rose-500"
            beta
            onClick={() => navigate('/legal-hr/staff-management')}
          />
          
          <LegalHRToolCard
            title="Recruitment Portal"
            description="Complete ATS system: post jobs, track applications, schedule interviews, AI resume screening, and manage your hiring pipeline."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>}
            color="from-violet-400 to-purple-500"
            beta
            onClick={() => navigate('/recruitment')}
          />
        </div>
        
        {/* Disclaimer */}
        <div className="mt-16 bg-yellow-50 border border-yellow-100 p-6 rounded-lg">
          <div className="flex items-start">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="font-bold text-yellow-800 mb-2">Legal Disclaimer</h4>
              <p className="text-yellow-700 text-sm">
                The documents generated by these tools are provided for informational purposes only and do not constitute legal advice. 
                While we strive to provide accurate and up-to-date information, laws and regulations vary by jurisdiction and change over time. 
                We recommend consulting with a qualified legal professional before implementing any legal document in your business operations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LegalHR;
